import React, {FC, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {
  BellIcon,
  BellSlashIcon,
  CheckIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";
import {useAuth} from "contexts/AuthContext";
import {FollowType} from "data/types";
import {
  useFollowOrganizerMutation,
  useGetFollowsQuery,
  useUnfollowOrganizerMutation,
  useUpdateFollowNotifyMutation,
} from "store/api";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonSecondary from "shared/Button/ButtonSecondary";

/**
 * Follow or unfollow an organizer and, once followed, mute or unmute its
 * notifications. Without a session, following asks to log in.
 */
export const FollowButton: FC<{organizerId: string}> = ({organizerId}) => {
  const {isLoggedIn} = useAuth();
  const navigate = useNavigate();
  const {data, isFetching} = useGetFollowsQuery(
    {target: organizerId},
    {skip: !isLoggedIn},
  );
  const [follow] = useFollowOrganizerMutation();
  const [unfollow] = useUnfollowOrganizerMutation();
  const [updateNotify, {isLoading: isUpdatingNotify}] =
    useUpdateFollowNotifyMutation();

  // From following or unfollowing until the follow is read again; muting
  // reads it again too, without touching this button.
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!isFetching) setBusy(false);
  }, [isFetching]);

  const toggleFollow = (action: typeof follow) => {
    setBusy(true);
    action(organizerId)
      .unwrap()
      .catch(() => setBusy(false));
  };

  const followDoc: FollowType | undefined = data?.data?.[0];
  const isFollowing = Number(data?.total) > 0 || !!followDoc;

  if (!isLoggedIn || !isFollowing) {
    return (
      <ButtonPrimary
        loading={busy}
        disabled={busy}
        onClick={() => (isLoggedIn ? toggleFollow(follow) : navigate("/login"))}
      >
        {!busy && <UserPlusIcon className="w-5 h-5 mr-2" />}
        Seguir
      </ButtonPrimary>
    );
  }

  const notify = followDoc?.notify ?? true;
  return (
    <div className="flex items-center gap-2">
      <ButtonSecondary
        loading={busy}
        disabled={busy}
        onClick={() => toggleFollow(unfollow)}
        className="group"
      >
        {!busy && <CheckIcon className="w-5 h-5 mr-2 group-hover:hidden" />}
        <span className="group-hover:hidden">Siguiendo</span>
        <span className="hidden group-hover:inline">Dejar de seguir</span>
      </ButtonSecondary>
      <button
        type="button"
        disabled={isUpdatingNotify}
        onClick={() => updateNotify({organizerId, notify: !notify})}
        aria-pressed={notify}
        aria-label={
          notify ? "Silenciar notificaciones" : "Activar notificaciones"
        }
        title={notify ? "Silenciar notificaciones" : "Activar notificaciones"}
        className="flex h-11 w-11 sm:h-12 sm:w-12 shrink-0 items-center justify-center rounded-full border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800 disabled:opacity-50"
      >
        {notify ? (
          <BellIcon className="w-5 h-5" />
        ) : (
          <BellSlashIcon className="w-5 h-5" />
        )}
      </button>
    </div>
  );
};
